// 备货单导出

import OrderSellerRemarksFlag from "@/enum/OrderSellerRemarksFlag";
import TaskStatusEnum from "@/enum/TaskStatus";
import mainServerClient, { baseURL } from "@/services/http/mainServerClient";
import { QueryOrderListParams } from "../order";

export enum ExcludeType {
    /** 排除延迟发货的订单 */
    EXCLUDE_ON_HOLD_PRINTED = "EXCLUDE_ON_HOLD_PRINTED",

    /** 排除已打印发货单的订单 */
    EXCLUDE_PACK_LIST_PRINTED = "EXCLUDE_PACK_LIST_PRINTED",

    /** 排除已打印快递单的订单 */
    EXCLUDE_WAYBILL_NO_PRINTED = "EXCLUDE_WAYBILL_NO_PRINTED",

    /** 排除不可达地区 */
    EXCLUDE_UNDELIVERABLE_AREA = "EXCLUDE_UNDELIVERABLE_AREA",

    /** 排除预发货订单 */
    EXCLUDE_PRE_SHIPPING = "EXCLUDE_PRE_SHIPPING",

    /** ----------------------退款中订单相关的选项（互斥）----------------- */

    /** 排除退款中的订单(排除退款中订单全部商品) */
    EXCLUDE_REFUNDING_PRINTED = "EXCLUDE_REFUNDING_PRINTED",
    /** 排除退款中的订单(仅排除退款中商品) */
    EXCLUDE_REFUNDING_ORDER_ITEM = "EXCLUDE_REFUNDING_ORDER_ITEM",

    /** --------------------------------------------------------- */
}

export enum PickListExportItem {
    ITEM_IMAGE = "ITEM_IMAGE",

    ITEM_TITLE = "ITEM_TITLE",

    PRODUCT_TITLE = "PRODUCT_TITLE",

    PRODUCT_SIMPLE_TITLE = "PRODUCT_SIMPLE_TITLE",

    SELLER_CODE = "SELLER_CODE",

    PRODUCT_ORIGINAL_IDS = "PRODUCT_ORIGINAL_IDS",

    STOCK_SITE = "STOCK_SITE",

    SIMPLE_TITLE = "SIMPLE_TITLE",

    SIMPLE_IMAGE = "SIMPLE_IMAGE",

    SIMPLE_PROPERTY = "SIMPLE_PROPERTY",

    SIMPLE_SELLER_CODE = "SIMPLE_SELLER_CODE",

    SIMPLE_PRICE = "SIMPLE_PRICE",

    QUANTITY = "QUANTITY",

    REFUNDING_QUANTITY = "REFUNDING_QUANTITY",

    SELLER_REMARK = "SELLER_REMARK",

    BUYER_REMARK = "BUYER_REMARK",

    TOTAL = "TOTAL",
}

export interface PickListExportTask {
    companyId: number;
    created: string;
    distributedTaskId: number;
    downloadUrl?: string;
    excludeTypes: ExcludeType[];
    exportItems: PickListExportItem[];
    id: number;
    orderIds: number[];
    paidEnd: string;
    paidStart: string;
    shopIds?: number[];
    status: "COMPLETED" | "ERROR" | "PENDING" | "RUNNING";
    modifyProductQuantity: boolean;
    sellerFlags: OrderSellerRemarksFlag[];
    anyProductSkuDeleted?: boolean;
    mergeRuleConfig: PickListMergeRuleConfig;
    markIds: number[];
    skuPropertiesFilterKeywords?: string;
    exportType: PickListExportType;
    shippingDeadlineHours?: number;
}

// 获取当前用户所有备货单导出的任务列表
export function getPickListExportTaskList(params: {
    pageNo?: number;
    pageSize?: number;
    ids?: string;
}): Promise<{ items: PickListExportTask[]; totalCount: number }> {
    return mainServerClient.get("/pickListExportTask/list", params);
}

interface GetPickListExportTaskParams {
    distributedId?: number;
    pageSize?: number;
    pageNo?: number;
}

/** 获取备货单导出记录分页列表 */
export function getExportPackList(params: GetPickListExportTaskParams) {
    return mainServerClient.post("/pickListExportTaskList", params);
}

export interface PickListOrderSellerRemarkModel {
    orderOriginalId: string;
    flag: OrderSellerRemarksFlag;
    content: string;
}

export interface PickListOrderBuyerRemarkModel {
    orderOriginalId: string;
    content: string;
}

export interface PickListProductSkuModel {
    externalId: string;
    name: string;
    originalId?: string;
    picture: string;
    price: number;
    quantity: number;
    /** 退款中单品数量，合并自各订单项中处于退款中的数量 */
    refundingQuantity: number;
    stockNo?: string;
    simpleTitle?: string;

    orderSellerRemarks: PickListOrderSellerRemarkModel[];
    orderBuyerRemarks: PickListOrderBuyerRemarkModel[];
}

export interface PickListProductModel {
    externalId: string;
    originalId: string;
    picture: string;
    skus: PickListProductSkuModel[];
    title: string; // 简称/标题
    total: number;
    rawTitle: string; // 标题
    simpleTitle: string; // 简称
    originalIds: string[];
}

export enum ProductSkuUpdatedInfoType {
    QUANTITY_MODIFIED = "QUANTITY_MODIFIED",
    DELETED = "DELETED",
}

export interface ProductSkuUpdatedInfo {
    productOriginalId: string;
    skuOriginalId: string;
    /**  删除不用传该值 */
    newQuantity?: number;
    updateType: ProductSkuUpdatedInfoType;
}

export enum ProductSortRule {
    DEFAULT = "DEFAULT",
    NAME_ASC = "NAME_ASC",
    EXTERNAL_ID_ASC = "EXTERNAL_ID_ASC",
    QUANTITY_DESC = "QUANTITY_DESC",
    BY_SKU_RULE = "BY_SKU_RULE",
}

export const ProductSortRuleNameMap = {
    [ProductSortRule.DEFAULT]: "按商品默认排序",
    [ProductSortRule.NAME_ASC]: "按商品简称/标题A-Z排序 ",
    [ProductSortRule.EXTERNAL_ID_ASC]: "按商品编码A-Z排序",
    [ProductSortRule.QUANTITY_DESC]: "按商品货位A-Z排序",
    [ProductSortRule.BY_SKU_RULE]: "仅按sku进行排序",
};

export enum SkuSortRule {
    DEFAULT = "DEFAULT",
    NAME_ASC = "NAME_ASC",
    EXTERNAL_ID_ASC = "EXTERNAL_ID_ASC",
    QUANTITY_DESC = "QUANTITY_DESC",
    PROPERTIES_COLOR_SIZE_ASC = "PROPERTIES_COLOR_SIZE_ASC",
}

export const SkuSortRuleNameMap = {
    [SkuSortRule.DEFAULT]: "按sku默认排序",
    [SkuSortRule.NAME_ASC]: "按sku属性A-Z排序",
    [SkuSortRule.EXTERNAL_ID_ASC]: "按sku编码A-Z排序",
    [SkuSortRule.QUANTITY_DESC]: "按sku货位A-Z排序",
    [SkuSortRule.PROPERTIES_COLOR_SIZE_ASC]: "按 SKU 属性颜色尺码排序",
};

export interface PickListSortRuleConfig {
    /**
     * 商品排序规则
     */
    productSortRule?: ProductSortRule;
    /**
     * 单品排序规则
     */
    skuSortRule?: SkuSortRule;
}

interface PickListExportTaskUpdateDto {
    /**导出时返回任务id */
    distributedId: number;

    /**
     * 用户修改过商品数量或删除的商品SKU信息列表,
     * 如果为null表示未修改
     */
    productSkuUpdatedInfos: ProductSkuUpdatedInfo[];

    /**
     * 排序规则配置，如果为 null 表示未修改
     */
    sortRuleConfig?: PickListSortRuleConfig;
}

export function exportPackList(params: PickListExportTaskUpdateDto): Promise<{ url: string }> {
    return mainServerClient.post("/pickListExportTask/export", params);
}

/** 更新备货单导出缓存结果 */
export function updateTaskSortRule(params: PickListExportTaskUpdateDto) {
    return mainServerClient.post<PackListTaskResult>("/pickListExportTask/update_task", params);
}

export enum PickListExportType {
    ALL_PENDING_SHIPMENT = "ALL_PENDING_SHIPMENT",
    PAYMENT_TIME = "PAYMENT_TIME",
    SHIPPING_DEADLINE = "SHIPPING_DEADLINE",
    SPECIFIED_ORDER = "SPECIFIED_ORDER",
    SPECIFIED_QUERY = "SPECIFIED_QUERY",
}

export interface MoreFeatureExportPackListParams {
    start?: string;
    end?: string;
    excludeTypes?: Omit<ExcludeType, "EXCLUDE_UNDELIVERABLE_AREA">[];
    exportItems?: PickListExportItem[];
    shopIds: number[];
    undeliverableArea?: {
        excludedCities: number[];
        excludedDistricts: number[];
        excludedProvinces: number[];
    };
    sellerFlags: OrderSellerRemarksFlag[];
    mergeRuleConfig: PickListMergeRuleConfig;
    markIds: number[];
    sortRuleConfig?: PickListSortRuleConfig;
    /** 是否根据过滤词自动删除单品属性内容 */
    autoFilterSkuProperties: boolean;
    exportType: PickListExportType;
    /** 发货截止时间筛选小时数，仅 exportType 为 SHIPPING_DEADLINE 时传递 */
    shippingDeadlineHours?: number;
}

export interface SelectedOrderTablePickListExportParams {
    exportItems: PickListExportItem[];
    mergeRuleConfig: PickListMergeRuleConfig;
    sellerFlags: OrderSellerRemarksFlag[];
    /** 是否根据过滤词自动删除单品属性内容 */
    autoFilterSkuProperties: boolean;
    exportType: PickListExportType.SPECIFIED_ORDER;

    orderIdsMap?: {
        [shopId: number]: number[];
    };
    excludedOrderItemIdsMap?: {
        [shop: number]: number[];
    };
}

export interface SearchAllOrderTablePickListExportParams {
    exportItems: PickListExportItem[];
    mergeRuleConfig: PickListMergeRuleConfig;
    /** 是否根据过滤词自动删除单品属性内容 */
    autoFilterSkuProperties: boolean;
    exportType: PickListExportType.SPECIFIED_QUERY;
    listParamsMap?: {
        [shop: number]: QueryOrderListParams;
    };
}

interface PackListTaskResult {
    distributedId: number;
    status: TaskStatusEnum;
    data: PickListProductModel[];
    errorMsg?: string;
}

export type OrderTablePackListExportParams =
    | SelectedOrderTablePickListExportParams
    | SearchAllOrderTablePickListExportParams;

type ExportPackListParamsType = MoreFeatureExportPackListParams | OrderTablePackListExportParams;

/** 提交导出备货单任务 */
export function generatePickListExportTask(params: ExportPackListParamsType): Promise<PackListTaskResult> {
    return mainServerClient.post("/pickListExportTask/generate_task", params);
}

/** 获取导出任务结果 */
export function getPickListExportTaskProcess(distributedId: number): Promise<PackListTaskResult> {
    return mainServerClient.get(`/pickListExportTask/generate_task/${distributedId}`);
}

/** 清理缓存中任务 (清理缓存中任务(导出备货单时，当买家没有选择导出而是直接选择打印时))*/
export function removePickListExportTask(distributedId: number) {
    return mainServerClient.post(`/pickListExportTask/remove/${distributedId}`);
}

/** 通过任务id获取导出备货单的url(订单列表中使用) */
export function getPackListDownloadUrlByDistributedId(
    distributedId: number,
): Promise<{ downloadUrl?: string; status: PackListTaskResult["status"] }> {
    return mainServerClient.get(`/pickListExportTask/export/${distributedId}`);
}

export interface PickListExportFieldsStatus {
    /** 是否勾选 */
    checked?: boolean;
    /** 备货单导出字段 */
    exportItem?: PickListExportItem;
}

export interface PickListSetting {
    exportItems?: PickListExportItem[];
    mergeRuleConfig?: PickListMergeRuleConfig;
    exportItemsStatus?: PickListExportFieldsStatus[];
    /** 是否根据过滤词自动删除单品属性内容 */
    autoFilterSkuProperties?: boolean;
}

// 获取当前用户的备货单设置
export function getPickListExportSetting(): Promise<PickListSetting> {
    return mainServerClient.get(`/stats/setting/pick_list_setting`);
}

/** 保存当前公司的备货单导出字段及排序 */
export function savePickListExportFieldsStatus(exportItemsStatus: PickListExportFieldsStatus[]): Promise<void> {
    return mainServerClient.post(`/stats/setting/v2/save_pick_list_export_fields`, {
        exportItemsStatus,
    });
}

export enum ProductMergeRule {
    /** 相同商品 ID 合并 (默认) */
    ORIGINAL_ID = "ORIGINAL_ID",
    /** 相同商品名称合并 */
    NAME = "NAME",
    /** 相同商家编码合并 */
    EXTERNAL_ID = "EXTERNAL_ID",
    /** 相同名称且相同商品 ID 合并 */
    NAME_AND_ORIGINAL_ID = "NAME_AND_ORIGINAL_ID",
    /** 相同名称且相同商家编码合并 */
    NAME_AND_EXTERNAL_ID = "NAME_AND_EXTERNAL_ID",
    /** 相同商品 ID 且相同商家编码合并 */
    ORIGINAL_ID_AND_EXTERNAL_ID = "ORIGINAL_ID_AND_EXTERNAL_ID",
    /** 相同商品 ID 且相同名称且相同商家编码合并 */
    ALL = "ALL",
    /** 仅按 SKU 合并规则合并 */
    SKU_RULE_ONLY = "SKU_RULE_ONLY",
}

export enum SkuMergeRule {
    /** 相同单品 ID 合并 */
    ORIGINAL_ID = "ORIGINAL_ID",
    /** 相同单品名称合并 */
    NAME = "NAME",
    /** 相同单品商家编码合并 */
    EXTERNAL_ID = "EXTERNAL_ID",
    /** 相同名称且(相同单品 ID 或相同商家编码)合并 (默认) */
    NAME_WITH_ORIGINAL_ID_OR_EXTERNAL_ID = "NAME_WITH_ORIGINAL_ID_OR_EXTERNAL_ID",
    /** 相同名称且相同单品 ID 合并 */
    NAME_AND_ORIGINAL_ID = "NAME_AND_ORIGINAL_ID",
    /** 相同名称且相同单品商家编码合并 */
    NAME_AND_EXTERNAL_ID = "NAME_AND_EXTERNAL_ID",
    /** 相同单品 ID 且相同单品商家编码合并 */
    ORIGINAL_ID_AND_EXTERNAL_ID = "ORIGINAL_ID_AND_EXTERNAL_ID",
    /** 相同单品 ID 且相同名称且相同单品商家编码合并 */
    ALL = "ALL",
}

export interface PickListMergeRuleConfig {
    /**
     * @deprecated
     */
    needIsolateByShop: boolean;
    productMergeRule: ProductMergeRule;
    skuMergeRule: SkuMergeRule;
}

export function savePickListMergeRuleConfig(mergeRuleConfig: PickListMergeRuleConfig): Promise<void> {
    return mainServerClient.post(`/stats/setting/save_picklist_merge_rule_config`, { mergeRuleConfig });
}

export function savePickListAutoFilterSkuProperties(autoFilterSkuProperties: boolean) {
    return mainServerClient.post(`/stats/setting/save_picklist_auto_filter_sku_properties`, {
        autoFilterSkuProperties,
    });
}
